import React, { useMemo } from "react"
import { cva } from "class-variance-authority"
import { ButtonWrapper } from "./ButtonWrapper"
import cn from "../../../utils/classes"
import { Typography, typographyTypes } from "./Typography"
import Icon from "./Icon"

export type BannerType =
  | "error"
  | "info"
  | "warning"
  | "success"
  | "actionNeeded"

export interface BannerProps {
  /** The type of the banner */
  type: BannerType
  /** The title of the banner */
  title?: string
  /** The content of the banner */
  content?: string
  /** The boolean that controls if the banner is visible or not */
  isOpen: boolean
  /** The callback function that is executed when the close button is clicked on the banner */
  onClose?: () => void
  /** The boolean that controls if the banner is sticky or not */
  styleOverrides?: string
  /** The custom styles that can be applied to the banner */
  titleStyleOverrides?: string
  /** The custom styles that can be applied to the banner content */
  contentStyleOverrides?: string
  /** The key for accessibility */
  id: string
  /** The boolean that controls if the banner is sticky or not */
  sticky?: boolean
  /** The role of the banner for accessibility. ex. 'alert' */
  ariaRole?: "alert" | "status"
}

export const Banner: React.FC<BannerProps> = ({
  type,
  title,
  content,
  isOpen,
  onClose,
  sticky,
  styleOverrides,
  titleStyleOverrides,
  contentStyleOverrides,
  id,
  ariaRole = "alert",
}) => {
  const accessibilityKey = id + "-banner-" + type

  const variants = cva(
    [
      "flex",
      "justify-between",
      "space-x-2",
      "items-center",
      "w-full",
      "rounded-lg",
      "p-4",
      "shadow-dark",
      "box-border",
    ],
    {
      variants: {
        type: {
          error: "bg-red-50",
          info: "bg-blue-50",
          warning: "bg-yellow-50",
          success: "bg-green-50",
          actionNeeded: "bg-red-50",
        },
        sticky: {
          true: "sticky top-0",
        },
        isOpen: {
          false: "hidden",
        },
      },
    },
  )

  const typographyVariants = cva([], {
    variants: {
      type: {
        error: "text-error-red",
        info: "text-info-blue",
        warning: "text-warning-orange",
        success: "text-green-800",
        actionNeeded: "text-error-red",
      },
    },
  })

  const className = useMemo(() => {
    return cn(variants({ type, sticky, isOpen }), styleOverrides)
  }, [variants, type, sticky, isOpen, styleOverrides])

  const titleClassName = useMemo(() => {
    return cn(typographyVariants({ type }), titleStyleOverrides)
  }, [type, titleStyleOverrides, typographyVariants])

  const contentClassName = useMemo(() => {
    return cn(typographyVariants({ type }), contentStyleOverrides)
  }, [contentStyleOverrides, type, typographyVariants])

  const iconMap: Record<BannerType, React.ReactNode> = {
    error: <Icon iconType="error" size="md" />,
    info: <Icon iconType="infoQuestionmark" size="md" />,
    warning: <Icon iconType="warningIcon" size="md" />,
    success: <Icon iconType="successCheckmark" size="md" />,
    actionNeeded: <Icon iconType="error" size="md" />,
  }

  const closeoutComponentMap: Record<BannerType, React.ReactNode> = {
    error: <Icon iconType="xRed" size="sm" />,
    info: <Icon iconType="xBlue" size="sm" />,
    warning: <Icon iconType="xOrange" size="sm" />,
    // TODO: Add x icons for success and action needed. See figma for SVGs
    success: <Icon iconType="x" size="sm" />,
    actionNeeded: <Icon iconType="xRed" size="sm" />,
  }

  return (
    <div
      className={className}
      id={accessibilityKey}
      role={ariaRole}
      aria-description={type + " banner"}
    >
      <div className="flex items-center space-x-2">
        {iconMap[type]}
        {title && (
          <Typography
            text={title}
            variant={typographyTypes.BANNER_TITLE}
            styleOverrides={titleClassName}
          />
        )}
        {content && (
          <Typography
            text={content}
            variant={typographyTypes.NORMAL}
            styleOverrides={contentClassName}
          />
        )}
      </div>
      {onClose && (
        <div className="flex-shrink-0 ml-auto">
          <ButtonWrapper
            onClick={onClose}
            backgroundColor="transparent"
            justification="end"
            ariaDescription={"close " + type + " banner"}
          >
            {closeoutComponentMap[type]}
          </ButtonWrapper>
        </div>
      )}
    </div>
  )
}
